import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteField,
  query,
  where,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { auth, db } from "../firebase/config";
import {
  App,
  CreateAppDTO,
  GooglePlayConnectionCheckResponse,
  UpdateAppDTO,
} from "../types/app";

const APPS_COLLECTION = "apps";
const DELETE_REGISTERED_APP_ENDPOINT =
  process.env.REACT_APP_DELETE_REGISTERED_APP_URL ||
  "https://europe-west1-translatorx-5774e.cloudfunctions.net/deleteRegisteredApp";
const VERIFY_GOOGLE_PLAY_CONNECTION_ENDPOINT =
  process.env.REACT_APP_VERIFY_GOOGLE_PLAY_CONNECTION_URL ||
  "https://europe-west1-translatorx-5774e.cloudfunctions.net/verifyGooglePlayConnection";

class AppService {
  private async postAuthenticatedJson<T>(
    url: string,
    body: Record<string, unknown>,
  ): Promise<T> {
    await auth.authStateReady();
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error("Authentication required. Please sign in.");
    }

    const idToken = await currentUser.getIdToken();
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify(body),
    });

    const rawText = await response.text();
    let responseData: T | { error?: string; message?: string } | null = null;
    try {
      responseData = rawText ? JSON.parse(rawText) : null;
    } catch {
      responseData = null;
    }

    if (!response.ok) {
      const detailedMessage =
        (responseData as any)?.error ||
        (responseData as any)?.message ||
        (rawText && rawText.trim().length > 0 ? rawText.trim() : null);
      const error = new Error(
        detailedMessage || `Request failed with status ${response.status}`,
      ) as Error & { responseData?: unknown };
      error.responseData = responseData;
      throw error;
    }

    return responseData as T;
  }

  /**
   * Generate a unique app ID
   */
  private generateAppId(): string {
    return `app_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }

  /**
   * Convert Firestore timestamp to Date
   */
  private convertTimestampToDate(timestamp: any): Date {
    if (timestamp instanceof Timestamp) {
      return timestamp.toDate();
    }
    if (timestamp?.toDate && typeof timestamp.toDate === "function") {
      return timestamp.toDate();
    }
    if (!timestamp) {
      return new Date();
    }
    const parsed = new Date(timestamp);
    return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
  }

  private stripUndefinedDeep(value: any): any {
    if (Array.isArray(value)) {
      return value
        .map((item) => this.stripUndefinedDeep(item))
        .filter((item) => item !== undefined);
    }

    if (value && typeof value === "object") {
      const cleanedEntries = Object.entries(value)
        .map(([key, entry]) => [key, this.stripUndefinedDeep(entry)] as const)
        .filter(([, entry]) => entry !== undefined);
      if (cleanedEntries.length === 0) return undefined;
      return Object.fromEntries(cleanedEntries);
    }

    return value === undefined ? undefined : value;
  }

  /**
   * Get all apps for a user
   */
  async getUserApps(userId: string): Promise<App[]> {
    try {
      const appsRef = collection(db, APPS_COLLECTION);
      const q = query(appsRef, where("userId", "==", userId));
      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map((doc) => {
        const data = doc.data();
        const androidStoreUrl = data.androidStoreUrl;
        const iosStoreUrl = data.iosStoreUrl;
        return {
          appId: data.appId,
          name: data.name,
          favorite: Boolean(data.favorite),
          description: data.description || "",
          category: data.category || "productivity",
          platform: data.platform,
          storeUrl:
            data.storeUrl ||
            (data.platform === "android"
              ? androidStoreUrl
              : data.platform === "ios"
              ? iosStoreUrl
              : androidStoreUrl || iosStoreUrl || ""),
          androidStoreUrl,
          iosStoreUrl,
          googlePlayPackageName: data.googlePlayPackageName || "",
          googlePlayDefaultLocale: data.googlePlayDefaultLocale || "",
          googlePlayLastPublishedAt: data.googlePlayLastPublishedAt
            ? this.convertTimestampToDate(data.googlePlayLastPublishedAt)
            : undefined,
          firebaseProjectId: data.firebaseProjectId || "",
          ga4PropertyId: data.ga4PropertyId || data.analyticsPropertyId || "",
          ga4StreamId: data.ga4StreamId || data.analyticsStreamId || "",
          storeId: data.storeId || "",
          privacyPolicyUrl: data.privacyPolicyUrl || "",
          termsOfServiceUrl: data.termsOfServiceUrl || "",
          researchContext: data.researchContext || undefined,
          createdAt: this.convertTimestampToDate(data.createdAt),
          updatedAt: this.convertTimestampToDate(data.updatedAt),
          userId: data.userId,
        } as App;
      });
    } catch (error) {
      console.error("Error fetching apps:", error);
      throw new Error("Failed to fetch apps");
    }
  }

  /**
   * Get a single app by ID
   */
  async getAppById(appId: string, userId: string): Promise<App | null> {
    try {
      const appsRef = collection(db, APPS_COLLECTION);
      const q = query(
        appsRef,
        where("appId", "==", appId),
        where("userId", "==", userId),
      );
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        return null;
      }

      const data = querySnapshot.docs[0].data();
      const androidStoreUrl = data.androidStoreUrl;
      const iosStoreUrl = data.iosStoreUrl;
      return {
        appId: data.appId,
        name: data.name,
        favorite: Boolean(data.favorite),
        description: data.description || "",
        category: data.category || "productivity",
        platform: data.platform,
        storeUrl:
          data.storeUrl ||
          (data.platform === "android"
            ? androidStoreUrl
            : data.platform === "ios"
            ? iosStoreUrl
            : androidStoreUrl || iosStoreUrl || ""),
        androidStoreUrl,
        iosStoreUrl,
        googlePlayPackageName: data.googlePlayPackageName || "",
        googlePlayDefaultLocale: data.googlePlayDefaultLocale || "",
        googlePlayLastPublishedAt: data.googlePlayLastPublishedAt
          ? this.convertTimestampToDate(data.googlePlayLastPublishedAt)
          : undefined,
        firebaseProjectId: data.firebaseProjectId || "",
        ga4PropertyId: data.ga4PropertyId || data.analyticsPropertyId || "",
        ga4StreamId: data.ga4StreamId || data.analyticsStreamId || "",
        storeId: data.storeId || "",
        privacyPolicyUrl: data.privacyPolicyUrl || "",
        termsOfServiceUrl: data.termsOfServiceUrl || "",
        researchContext: data.researchContext || undefined,
        createdAt: this.convertTimestampToDate(data.createdAt),
        updatedAt: this.convertTimestampToDate(data.updatedAt),
        userId: data.userId,
      } as App;
    } catch (error) {
      console.error("Error fetching app:", error);
      throw new Error("Failed to fetch app");
    }
  }

  /**
   * Create a new app
   */
  async createApp(userId: string, appData: CreateAppDTO): Promise<App> {
    try {
      const appId = this.generateAppId();
      const now = serverTimestamp();
      const resolvedAndroidStoreUrl =
        appData.platform === "android"
          ? appData.storeUrl
          : appData.platform === "both"
          ? appData.androidStoreUrl
          : undefined;
      const resolvedIosStoreUrl =
        appData.platform === "ios"
          ? appData.storeUrl
          : appData.platform === "both"
          ? appData.iosStoreUrl
          : undefined;
      const storeUrl =
        appData.platform === "android"
          ? resolvedAndroidStoreUrl
          : appData.platform === "ios"
          ? resolvedIosStoreUrl
          : resolvedAndroidStoreUrl ||
            resolvedIosStoreUrl ||
            appData.storeUrl ||
            "";

      const newApp = {
        appId,
        name: appData.name,
        favorite: Boolean(appData.favorite),
        description: appData.description,
        category: appData.category,
        platform: appData.platform,
        storeUrl,
        ...(resolvedAndroidStoreUrl
          ? { androidStoreUrl: resolvedAndroidStoreUrl }
          : {}),
        ...(resolvedIosStoreUrl ? { iosStoreUrl: resolvedIosStoreUrl } : {}),
        ...(appData.googlePlayPackageName
          ? { googlePlayPackageName: appData.googlePlayPackageName }
          : {}),
        ...(appData.googlePlayDefaultLocale
          ? { googlePlayDefaultLocale: appData.googlePlayDefaultLocale }
          : {}),
        ...(appData.firebaseProjectId
          ? { firebaseProjectId: appData.firebaseProjectId }
          : {}),
        ...(appData.ga4PropertyId ? { ga4PropertyId: appData.ga4PropertyId } : {}),
        ...(appData.ga4StreamId ? { ga4StreamId: appData.ga4StreamId } : {}),
        storeId: appData.storeId || "",
        ...(appData.privacyPolicyUrl
          ? { privacyPolicyUrl: appData.privacyPolicyUrl }
          : {}),
        ...(appData.termsOfServiceUrl
          ? { termsOfServiceUrl: appData.termsOfServiceUrl }
          : {}),
        ...(appData.researchContext
          ? { researchContext: appData.researchContext }
          : {}),
        createdAt: now,
        updatedAt: now,
        userId,
      };
      const sanitizedNewApp = this.stripUndefinedDeep(newApp);
      await addDoc(collection(db, APPS_COLLECTION), sanitizedNewApp);

      return {
        ...newApp,
        createdAt: new Date(),
        updatedAt: new Date(),
      } as App;
    } catch (error) {
      console.error("Error creating app:", error);
      throw new Error("Failed to create app");
    }
  }

  /**
   * Update an existing app
   */
  async updateApp(
    appId: string,
    userId: string,
    updates: UpdateAppDTO,
  ): Promise<void> {
    try {
      const appsRef = collection(db, APPS_COLLECTION);
      const q = query(
        appsRef,
        where("appId", "==", appId),
        where("userId", "==", userId),
      );
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        throw new Error("App not found");
      }

      const docRef = querySnapshot.docs[0].ref;
      const updatePayload: Record<string, any> = {
        updatedAt: serverTimestamp(),
      };

      Object.entries(updates).forEach(([key, value]) => {
        if (value === undefined) {
          updatePayload[key] = deleteField();
          return;
        }

        if (value && typeof value === "object" && !Array.isArray(value)) {
          const cleaned = this.stripUndefinedDeep(value);
          updatePayload[key] = cleaned === undefined ? deleteField() : cleaned;
          return;
        }

        updatePayload[key] = value;
      });

      await updateDoc(docRef, updatePayload);
    } catch (error) {
      console.error("Error updating app:", error);
      throw new Error("Failed to update app");
    }
  }

  /**
   * Delete an app
   */
  async deleteApp(appId: string, userId: string): Promise<void> {
    try {
      if (!appId?.trim()) {
        throw new Error("App ID is required");
      }
      if (!userId?.trim()) {
        throw new Error("User ID is required");
      }

      await this.postAuthenticatedJson(DELETE_REGISTERED_APP_ENDPOINT, {
        appId: appId.trim(),
      });
    } catch (error) {
      console.error("Error deleting app:", error);
      throw new Error("Failed to delete app");
    }
  }

  async verifyGooglePlayConnection(
    packageName: string,
  ): Promise<GooglePlayConnectionCheckResponse> {
    return this.postAuthenticatedJson<GooglePlayConnectionCheckResponse>(
      VERIFY_GOOGLE_PLAY_CONNECTION_ENDPOINT,
      {
        packageName: packageName.trim(),
      },
    );
  }

  /**
   * Validate store URL format
   */
  validateStoreUrl(url: string, platform: "android" | "ios" | "both"): boolean {
    if (platform === "android" || platform === "both") {
      const playStoreRegex =
        /^https?:\/\/play\.google\.com\/store\/apps\/details\?id=/;
      if (playStoreRegex.test(url)) return true;
    }

    if (platform === "ios" || platform === "both") {
      const appStoreRegex =
        /^https?:\/\/apps\.apple\.com\/(?:[a-z]{2}(?:-[A-Z]{2})?\/)?app(?:\/[^/]+)?\/id\d+(?:[/?#].*)?$/i;
      if (appStoreRegex.test(url)) return true;
    }

    return false;
  }
}

export default new AppService();
